import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUser } from './useUser';
import { useCheckIntegration } from '@auth_module/hooks/useCheckIntegration';
import { STORAGE_KEY } from '@auth_module/constants/storageKeys';
import {
  REGISTRATION_CTX_KEYS,
  REGISTRATION_STORAGE_KEY,
} from '@account_module/constants/registrationCtxKeys';
import { useAuthContext } from '@auth_module/context/AuthContext';

export const useRedirect = () => {
  const isRedirectedRef = useRef(false);
  const navigate = useNavigate();
  const { auth } = useAuthContext();
  const { isLoading, isLoadingOrganization, organization, user } = useUser();
  const { shouldRedirectToOnboarding, isIntegrationLoading, redirectToOnboarding } =
    useCheckIntegration(organization);

  const storageRegCtx = window.localStorage.getItem(REGISTRATION_STORAGE_KEY.CTX);
  const registrationContext = storageRegCtx ? JSON.parse(storageRegCtx) : null;

  const storagePersistedPathname = window.localStorage.getItem(STORAGE_KEY.persisted_pathname);
  const persistedPathname = storagePersistedPathname ? JSON.parse(storagePersistedPathname) : null;

  const navigateToDashboard = useCallback(() => {
    // 🔒 If not authenticated, do nothing — prevents redirects after logout
    if (!auth?.token) return;

    if (
      isRedirectedRef.current ||
      isLoading ||
      isLoadingOrganization ||
      !organization?.type ||
      isIntegrationLoading
    ) {
      return;
    }

    if (!user?.email_verified) {
      navigate('/email/verify', { replace: true });
      return;
    }

    if (shouldRedirectToOnboarding) {
      redirectToOnboarding(organization?.type);
      return;
    }

    if (organization.type === 'customer-free' || organization.type === 'customer') {
      const pathname =
        registrationContext === REGISTRATION_CTX_KEYS.SELF_SERVICE_API
          ? '/account/self-service-api'
          : '/dashboard';

      const _pathname = persistedPathname ? persistedPathname : pathname;
      navigate(_pathname, { replace: true });

      window.localStorage.removeItem(STORAGE_KEY.persisted_pathname);
      window.localStorage.removeItem(REGISTRATION_STORAGE_KEY.CTX);
    }

    isRedirectedRef.current = true;
  }, [
    auth?.token,
    isLoading,
    isLoadingOrganization,
    organization,
    isIntegrationLoading,
    user?.email_verified,
    shouldRedirectToOnboarding,
    redirectToOnboarding,
    persistedPathname,
    registrationContext,
    navigate,
  ]);

  return {
    navigateToDashboard,
    isLoading: !isLoading && (isLoadingOrganization || isIntegrationLoading),
  };
};
